/** Route templates registered by the backend. Keep framework syntax visible at the call site. */
const API_ROUTES = Object.freeze({
  health: '/health',
  vehicles: '/api/vehicles',
  vehicle: '/api/vehicles/:vehicleId',
  routeVehicles: '/api/routes/:routeId/vehicles',
  tripUpdates: '/api/trips/:tripId/updates',
  busVehicleStops: '/api/bus-vehicles/:ref/stops',
  alerts: '/api/alerts',
  routeAlerts: '/api/alerts/route/:routeId',
  stops: '/api/stops',
  busStops: '/api/bus-stops',
  busRoutes: '/api/bus-routes',
  busStopArrivals: '/api/bus-stops/:id/arrivals',
  stopCoords: '/api/stops/coords',
  liveStationRoutes: '/api/stops/live-routes',
  stopsWithRoutes: '/api/stops/with-routes',
  stationDetails: '/api/stops/:id/details',
  stopArrivals: '/api/stops/:id/arrivals',
  crowding: '/api/crowding',
  vehicleCrowding: '/api/crowding/:vehicleId',
  geocode: '/api/geocode',
  routeShapes: '/api/routes/shapes',
  busRouteShapes: '/api/bus-routes/shapes',
  systemStatus: '/api/system/status',
  tripPlan: '/api/trip-plan',
  widgetArrivals: '/api/widget/arrivals',
  widgetAlerts: '/api/widget/alerts',
  ferryNetwork: '/api/ferry/network',
  ferryStopDepartures: '/api/ferry/stops/:id/departures',
  ferryTrip: '/api/ferry/trips/:tripId',
  railNetwork: '/api/rail/network',
  railStationDepartures: '/api/rail/stations/:id/departures',
  railTrip: '/api/rail/trips/:tripId',
  njBusNetwork: '/api/njbus/network',
  njBusStopDepartures: '/api/njbus/stops/:id/departures',
  njBusTrip: '/api/njbus/trips/:tripId',
})

const segment = (value) => encodeURIComponent(value)

function pointParams(params, prefix, point) {
  if ('stopId' in point) params.set(`${prefix}StopId`, point.stopId)
  else {
    params.set(`${prefix}Lat`, point.lat.toFixed(6))
    params.set(`${prefix}Lon`, point.lon.toFixed(6))
  }
}

/** Client-side path builders paired with API_ROUTES. */
const apiPath = Object.freeze({
  health: () => API_ROUTES.health,
  vehicles: () => API_ROUTES.vehicles,
  vehicle: (id) => `/api/vehicles/${segment(id)}`,
  routeVehicles: (routeId) => `/api/routes/${segment(routeId)}/vehicles`,
  tripUpdates: (tripId) => `/api/trips/${segment(tripId)}/updates`,
  busVehicleStops: (ref) => `/api/bus-vehicles/${segment(ref)}/stops`,
  alerts: (scope) => (scope === 'upcoming' ? `${API_ROUTES.alerts}?scope=upcoming` : API_ROUTES.alerts),
  routeAlerts: (routeId) => `/api/alerts/route/${segment(routeId)}`,
  stops: () => API_ROUTES.stops,
  busStops: () => API_ROUTES.busStops,
  busRoutes: () => API_ROUTES.busRoutes,
  busStopArrivals: (id) => `/api/bus-stops/${segment(id)}/arrivals`,
  stopCoords: () => API_ROUTES.stopCoords,
  liveStationRoutes: () => API_ROUTES.liveStationRoutes,
  stopsWithRoutes: () => API_ROUTES.stopsWithRoutes,
  stationDetails: (id) => `/api/stops/${segment(id)}/details`,
  stopArrivals: (id, times) =>
    `/api/stops/${segment(id)}/arrivals${times ? `?times=${times}` : ''}`,
  crowding: (vehicleId) => `/api/crowding/${segment(vehicleId)}`,
  reportCrowding: () => API_ROUTES.crowding,
  geocode: (text) => `${API_ROUTES.geocode}?text=${segment(text)}`,
  routeShapes: () => API_ROUTES.routeShapes,
  busRouteShapes: () => API_ROUTES.busRouteShapes,
  systemStatus: () => API_ROUTES.systemStatus,
  tripPlan: ({ from, to, departAt, modes, expressBus }) => {
    const params = new URLSearchParams()
    pointParams(params, 'from', from)
    pointParams(params, 'to', to)
    if (departAt) params.set('departAt', String(departAt))
    if (modes) params.set('modes', modes)
    if (expressBus === false) params.set('expressBus', 'false')
    return `${API_ROUTES.tripPlan}?${params}`
  },
  widgetArrivals: () => API_ROUTES.widgetArrivals,
  widgetAlerts: () => API_ROUTES.widgetAlerts,
  ferryNetwork: () => API_ROUTES.ferryNetwork,
  ferryStopDepartures: (id, hours) =>
    `/api/ferry/stops/${segment(id)}/departures${hours ? `?hours=${hours}` : ''}`,
  ferryTrip: (tripId) => `/api/ferry/trips/${segment(tripId)}`,
  railNetwork: () => API_ROUTES.railNetwork,
  railStationDepartures: (id, hours) =>
    `/api/rail/stations/${segment(id)}/departures${hours ? `?hours=${hours}` : ''}`,
  railTrip: (tripId) => `/api/rail/trips/${segment(tripId)}`,
  njBusNetwork: () => API_ROUTES.njBusNetwork,
  njBusStopDepartures: (id, hours) =>
    `/api/njbus/stops/${segment(id)}/departures${hours ? `?hours=${hours}` : ''}`,
  njBusTrip: (tripId) => `/api/njbus/trips/${segment(tripId)}`,
})

module.exports = { API_ROUTES, apiPath }
